/**
 * Data Merge Monitor Component
 * Provides real-time monitoring and control for data merging operations
 */

import * as React from 'react';
import { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  LinearProgress,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Alert,
  IconButton,
  Tooltip,
  Grid,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Refresh as RefreshIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  CheckCircle as CheckCircleIcon,
  Info as InfoIcon,
  Speed as SpeedIcon,
  Storage as StorageIcon,
  Timeline as TimelineIcon,
} from '@mui/icons-material';
import { useDataMerging, useMergeMonitoring } from '../../hooks/useDataMerging';
import { validateTrackingData, createValidationReport, type ValidationResult } from '../../utils/dataValidation';
import type { EnhancedMessageTrackingData } from '../../services/outreach/types';

interface DataMergeMonitorProps {
  /** Campaign ID to monitor */
  campaignId?: string;
  /** Whether to show detailed performance metrics */
  showPerformanceMetrics?: boolean;
  /** Whether to auto-refresh monitoring data */
  autoRefresh?: boolean;
  /** Refresh interval in milliseconds */
  refreshInterval?: number;
  /** Callback when validation issues are detected */
  onValidationIssues?: (result: ValidationResult) => void;
}

interface MergeOperation {
  id: string;
  campaignId: string;
  status: 'pending' | 'running' | 'completed' | 'failed';
  startTime: number;
  endTime?: number;
  itemsProcessed: number;
  totalItems: number;
  conflicts: number;
  errors?: string[];
}

export const DataMergeMonitor: React.FC<DataMergeMonitorProps> = ({
  campaignId,
  showPerformanceMetrics = false,
  autoRefresh = true,
  refreshInterval = 5000,
  onValidationIssues,
}) => {
  const [operations, setOperations] = useState<MergeOperation[]>([]);
  const [validationResult, setValidationResult] = useState<ValidationResult | null>(null);
  const [showValidationDialog, setShowValidationDialog] = useState(false);
  const [isValidating, setIsValidating] = useState(false);

  const { getPerformanceMetrics, resetPerformanceMetrics } = useDataMerging({
    enablePerformanceMonitoring: true,
    onMergeComplete: (result) => {
      // Update operations list when merge completes
      setOperations(prev => prev.map(op => 
        op.status === 'running' 
          ? { 
              ...op, 
              status: 'completed', 
              endTime: Date.now(),
              conflicts: result.conflicts?.length || 0,
              itemsProcessed: result.data.length 
            }
          : op
      ));
    },
    onMergeError: (error) => {
      // Update operations list when merge fails
      setOperations(prev => prev.map(op => 
        op.status === 'running' 
          ? { 
              ...op, 
              status: 'failed', 
              endTime: Date.now(),
              errors: [error.message] 
            }
          : op
      ));
    },
  });

  const { getGlobalMetrics } = useMergeMonitoring();

  // Auto-refresh effect
  useEffect(() => {
    if (!autoRefresh) return;

    const interval = setInterval(() => {
      // Update performance metrics display
      triggerUpdate();
    }, refreshInterval);

    return () => clearInterval(interval);
  }, [autoRefresh, refreshInterval]);

  // Force component re-render to update metrics
  const [, forceUpdate] = useState({});
  const triggerUpdate = useCallback(() => forceUpdate({}), []);

  const performanceMetrics = getPerformanceMetrics();
  const globalMetrics = getGlobalMetrics();

  const handleValidateData = async () => {
    if (!campaignId) return;

    setIsValidating(true);
    try {
      // This would typically get data from React Query cache
      // For now, we'll simulate validation
      const mockData: EnhancedMessageTrackingData[] = []; // Get from cache
      const result = validateTrackingData(mockData);
      
      setValidationResult(result);
      setShowValidationDialog(true);
      
      if (onValidationIssues && (!result.isValid || result.warnings.length > 0)) {
        onValidationIssues(result);
      }
    } catch (error) {
      console.error('Validation failed:', error);
    } finally {
      setIsValidating(false);
    }
  };

  const handleResetMetrics = () => {
    resetPerformanceMetrics();
    setOperations([]);
    triggerUpdate();
  };

  const getStatusColor = (status: MergeOperation['status']) => {
    switch (status) {
      case 'completed': return 'success';
      case 'failed': return 'error';
      case 'running': return 'warning';
      default: return 'default';
    }
  };

  const getStatusIcon = (status: MergeOperation['status']) => {
    switch (status) {
      case 'completed': return <CheckCircleIcon />;
      case 'failed': return <ErrorIcon />;
      case 'running': return <RefreshIcon className="animate-spin" />;
      default: return <InfoIcon />;
    }
  };

  return (
    <Box sx={{ p: 2 }}>
      <Grid container spacing={2}>
        {/* Performance Metrics Card */}
        {showPerformanceMetrics && (
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                  <Typography variant="h6" display="flex" alignItems="center" gap={1}>
                    <SpeedIcon />
                    Performance Metrics
                  </Typography>
                  <Tooltip title="Reset metrics">
                    <IconButton onClick={handleResetMetrics} size="small">
                      <RefreshIcon />
                    </IconButton>
                  </Tooltip>
                </Box>

                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">
                      Total Operations
                    </Typography>
                    <Typography variant="h4">
                      {performanceMetrics.totalOperations}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">
                      Average Time
                    </Typography>
                    <Typography variant="h4">
                      {performanceMetrics.averageTime.toFixed(1)}ms
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">
                      Largest Dataset
                    </Typography>
                    <Typography variant="h4">
                      {performanceMetrics.largestDataset}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">
                      Conflicts Resolved
                    </Typography>
                    <Typography variant="h4">
                      {performanceMetrics.conflictsResolved}
                    </Typography>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        )}

        {/* Data Validation Card */}
        <Grid item xs={12} md={showPerformanceMetrics ? 6 : 12}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                <Typography variant="h6" display="flex" alignItems="center" gap={1}>
                  <StorageIcon />
                  Data Validation
                </Typography>
                <Button
                  variant="outlined"
                  onClick={handleValidateData}
                  disabled={isValidating || !campaignId}
                  startIcon={isValidating ? <RefreshIcon className="animate-spin" /> : <CheckCircleIcon />}
                >
                  {isValidating ? 'Validating...' : 'Validate Data'}
                </Button>
              </Box>

              {validationResult && (
                <Box>
                  <Alert 
                    severity={validationResult.isValid ? 'success' : 'error'}
                    sx={{ mb: 2 }}
                  >
                    {validationResult.isValid 
                      ? `Data validation passed (${validationResult.summary.validItems} items)`
                      : `Data validation failed (${validationResult.summary.errorCount} errors, ${validationResult.summary.warningCount} warnings)`
                    }
                  </Alert>

                  <Grid container spacing={2}>
                    <Grid item xs={3}>
                      <Typography variant="body2" color="text.secondary">Total Items</Typography>
                      <Typography variant="h6">{validationResult.summary.totalItems}</Typography>
                    </Grid>
                    <Grid item xs={3}>
                      <Typography variant="body2" color="text.secondary">Valid Items</Typography>
                      <Typography variant="h6" color="success.main">{validationResult.summary.validItems}</Typography>
                    </Grid>
                    <Grid item xs={3}>
                      <Typography variant="body2" color="text.secondary">Errors</Typography>
                      <Typography variant="h6" color="error.main">{validationResult.summary.errorCount}</Typography>
                    </Grid>
                    <Grid item xs={3}>
                      <Typography variant="body2" color="text.secondary">Warnings</Typography>
                      <Typography variant="h6" color="warning.main">{validationResult.summary.warningCount}</Typography>
                    </Grid>
                  </Grid>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Recent Operations */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" display="flex" alignItems="center" gap={1} mb={2}>
                <TimelineIcon />
                Recent Merge Operations
              </Typography>

              {operations.length === 0 ? (
                <Typography color="text.secondary" textAlign="center" py={2}>
                  No recent merge operations
                </Typography>
              ) : (
                <List>
                  {operations.slice(-5).reverse().map((operation) => (
                    <ListItem key={operation.id} divider>
                      <ListItemIcon>
                        {getStatusIcon(operation.status)}
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <Box display="flex" alignItems="center" gap={1}>
                            <Typography variant="body1">
                              Campaign {operation.campaignId}
                            </Typography>
                            <Chip 
                              label={operation.status} 
                              color={getStatusColor(operation.status)}
                              size="small"
                            />
                          </Box>
                        }
                        secondary={
                          <Box>
                            <Typography variant="body2" color="text.secondary">
                              {operation.itemsProcessed}/{operation.totalItems} items processed
                              {operation.conflicts > 0 && ` • ${operation.conflicts} conflicts`}
                              {operation.endTime && ` • ${operation.endTime - operation.startTime}ms`}
                            </Typography>
                            {operation.status === 'running' && (
                              <LinearProgress 
                                variant="determinate" 
                                value={(operation.itemsProcessed / operation.totalItems) * 100}
                                sx={{ mt: 1 }}
                              />
                            )}
                            {operation.errors && operation.errors.length > 0 && (
                              <Alert severity="error" sx={{ mt: 1 }}>
                                {operation.errors.join(', ')}
                              </Alert>
                            )}
                          </Box>
                        }
                      />
                    </ListItem>
                  ))}
                </List>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Validation Details Dialog */}
      <Dialog 
        open={showValidationDialog} 
        onClose={() => setShowValidationDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Data Validation Report</DialogTitle>
        <DialogContent>
          {validationResult && (
            <Box>
              {/* Summary */}
              <Alert 
                severity={validationResult.isValid ? 'success' : 'error'}
                sx={{ mb: 2 }}
              >
                {validationResult.isValid ? 'All data passed validation' : 'Data validation issues detected'}
              </Alert>

              {/* Errors */}
              {validationResult.errors.length > 0 && (
                <Accordion>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography color="error">
                      Errors ({validationResult.errors.length})
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <List>
                      {validationResult.errors.map((error, index) => (
                        <ListItem key={index}>
                          <ListItemIcon>
                            <ErrorIcon color="error" />
                          </ListItemIcon>
                          <ListItemText
                            primary={error.message}
                            secondary={`Field: ${error.field || 'N/A'} • Severity: ${error.severity}`}
                          />
                        </ListItem>
                      ))}
                    </List>
                  </AccordionDetails>
                </Accordion>
              )}

              {/* Warnings */}
              {validationResult.warnings.length > 0 && (
                <Accordion>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography color="warning.main">
                      Warnings ({validationResult.warnings.length})
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <List>
                      {validationResult.warnings.map((warning, index) => (
                        <ListItem key={index}>
                          <ListItemIcon>
                            <WarningIcon color="warning" />
                          </ListItemIcon>
                          <ListItemText
                            primary={warning.message}
                            secondary={warning.suggestion}
                          />
                        </ListItem>
                      ))}
                    </List>
                  </AccordionDetails>
                </Accordion>
              )}

              {/* Raw Report */}
              <Accordion>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography>Raw Validation Report</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Box 
                    component="pre" 
                    sx={{ 
                      fontSize: '0.75rem', 
                      backgroundColor: 'grey.100', 
                      p: 2, 
                      borderRadius: 1,
                      overflow: 'auto',
                      maxHeight: 300,
                    }}
                  >
                    {createValidationReport(validationResult)}
                  </Box>
                </AccordionDetails>
              </Accordion>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowValidationDialog(false)}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DataMergeMonitor;